import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { 
  Calendar, 
  Umbrella, 
  Flag, 
  ChevronRight,
  Sun,
  PartyPopper
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Link } from "react-router-dom";

const PERIOD_OPTIONS = [
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" }
];

export default function OperationalFeed() {
  const [period, setPeriod] = useState("week");
  const [ptos, setPtos] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOperationalData();
  }, [period]);

  const fetchOperationalData = async () => {
    setLoading(true);
    try {
      // Calculate date range based on period
      const today = new Date();
      const endDate = new Date();
      if (period === "week") {
        endDate.setDate(today.getDate() + 7);
      } else {
        endDate.setDate(today.getDate() + 30);
      }
      
      const startStr = today.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      const [ptosRes, deliverablesRes] = await Promise.all([
        apiClient.get(`/leaves?status=approved&start_date=${startStr}&end_date=${endStr}`).catch(() => ({ data: [] })),
        apiClient.get(`/today/dashboard`).catch(() => ({ data: {} }))
      ]);

      // Process PTOs
      const ptosData = (ptosRes.data || []).map(pto => ({
        id: pto.id,
        name: pto.resource_name || pto.user_name || "Unknown",
        type: pto.leave_type || "PTO",
        startDate: pto.start_date,
        endDate: pto.end_date,
        days: calculateDays(pto.start_date, pto.end_date)
      }));
      setPtos(ptosData);

      // Process deliverables as deadlines
      const delivs = deliverablesRes.data?.deliverables_due_soon || [];
      setDeadlines(delivs.map(d => ({
        id: d.id,
        title: d.deliverable_type,
        programName: d.program_name,
        programId: d.program_id,
        dueDate: d.due_window
      })));

      // Static holidays (can be extended to fetch from backend)
      const upcomingHolidays = getUpcomingHolidays(today, endDate);
      setHolidays(upcomingHolidays);

    } catch (err) {
      console.error("Failed to fetch operational data", err);
    }
    setLoading(false);
  };

  const calculateDays = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    return Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
  };

  const getUpcomingHolidays = (start, end) => {
    // Company holidays - can be moved to backend
    const holidays2026 = [
      { name: "New Year's Day", date: "2026-01-01" },
      { name: "MLK Day", date: "2026-01-19" },
      { name: "Presidents' Day", date: "2026-02-16" },
      { name: "Good Friday", date: "2026-04-03" },
      { name: "Memorial Day", date: "2026-05-25" },
      { name: "Independence Day", date: "2026-07-04" },
      { name: "Labor Day", date: "2026-09-07" },
      { name: "Thanksgiving", date: "2026-11-26" },
      { name: "Christmas Eve", date: "2026-12-24" },
      { name: "Christmas Day", date: "2026-12-25" },
    ];
    
    return holidays2026.filter(h => {
      const d = new Date(h.date);
      return d >= start && d <= end;
    }).map(h => ({
      ...h,
      id: h.date,
      daysUntil: Math.ceil((new Date(h.date) - new Date()) / (1000 * 60 * 60 * 24))
    }));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const totalItems = ptos.length + holidays.length + deadlines.length;

  if (loading) {
    return (
      <Card data-testid="operational-feed">
        <CardContent className="py-8 text-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="operational-feed" className="border-green-200 bg-green-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-green-600" strokeWidth={1.5} />
            <CardTitle className="text-[10px] font-medium uppercase tracking-wider text-green-700">
              Operational Feed
            </CardTitle>
            <Badge className="rounded-full bg-green-100 text-green-700 border-green-200">
              {totalItems}
            </Badge>
          </div>
          {/* Period Toggle */}
          <div className="flex items-center gap-1 bg-green-100/50 rounded-full p-0.5">
            {PERIOD_OPTIONS.map(opt => (
              <Button
                key={opt.id}
                variant={period === opt.id ? "default" : "ghost"}
                size="sm"
                onClick={() => setPeriod(opt.id)}
                className={`h-6 px-2 text-xs rounded-full ${
                  period === opt.id ? "bg-green-600 text-white" : "text-green-700 hover:text-green-800"
                }`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {totalItems === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No upcoming events {period === "week" ? "this week" : "this month"}
          </p>
        ) : (
          <>
            {/* PTOs */}
            {ptos.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Umbrella className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs font-medium text-muted-foreground">Time Off ({ptos.length})</span>
                </div>
                <div className="space-y-1.5">
                  {ptos.slice(0, 5).map(pto => (
                    <div 
                      key={pto.id}
                      className="flex items-center justify-between text-sm py-3 px-1 border-t border-border first:border-t-0 hover:bg-muted/60 transition-colors duration-250 ease-brand group"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{pto.name}</span>
                        <Badge variant="outline" className="text-[10px]">{pto.type}</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(pto.startDate)} ({pto.days}d)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Holidays */}
            {holidays.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Sun className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-xs font-medium text-muted-foreground">Holidays ({holidays.length})</span>
                </div>
                <div className="space-y-1.5">
                  {holidays.map(holiday => (
                    <div 
                      key={holiday.id}
                      className="flex items-center justify-between text-sm py-3 px-1 border-t border-border first:border-t-0 hover:bg-muted/60 transition-colors duration-250 ease-brand group"
                    >
                      <div className="flex items-center gap-2">
                        <PartyPopper className="w-3.5 h-3.5 text-amber-500" />
                        <span className="font-medium">{holiday.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(holiday.date)} ({holiday.daysUntil}d)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deadlines */}
            {deadlines.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Flag className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-xs font-medium text-muted-foreground">Deadlines ({deadlines.length})</span>
                </div>
                <div className="space-y-1.5">
                  {deadlines.slice(0, 5).map(deadline => (
                    <Link
                      key={deadline.id}
                      to={`/programs/${deadline.programId}`}
                      className="flex items-center justify-between text-sm py-3 px-1 border-t border-terracotta-200 first:border-t-0 hover:bg-muted/60 transition-colors group"
                    >
                      <div>
                        <span className="font-medium group-hover:text-red-600 transition-colors">{deadline.title}</span>
                        <p className="text-xs text-muted-foreground">{deadline.programName}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600">{formatDate(deadline.dueDate)}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-red-600" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
